/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable max-lines */
import { useContext, useState, useRef, useEffect, useCallback } from "react";
import { ApiContext, CheckoutActionTypes, CheckoutContext, getBridgeSdk } from "@swiggy-private/checkout-core";
// helpers
import { isEmpty, isNetworkError, useMountedRef } from "@swiggy-private/core-ui";
import {
    convertNativeGiftingCartToUpdateCartAPIPayload,
    generateNativeLocalGiftingCartFromStates,
} from "@cart-client/helpers/GiftingCart";
import { getGiftingCart, updateGiftingCart } from "@cart-client/apis/GiftingCart";
// types
import { GiftingReviewedCart } from "@cart-client/interfaces/GiftingCart";
import { Ok, Err, Result } from "@swiggy-private/js-fp";
import { ApiResponse } from "@cart-client/interfaces/Api";
import { GiftingLocalCart, GiftingCartApiResponse } from "@swiggy-private/cart-types";
import { HTTP_STATUS_CODES } from "@cart-client/constants/index";
import {
    UPDATE_GIFTING_CART_API_DEFAULT_ERROR,
    GIFTING_CART_API_DEFAULT_ERROR_MESSAGES,
} from "@cart-client/constants/GiftingCart";
import { MARKETPLACE_BUSINESS_LINE, MARKETPLACE_CATEGORY, MARKETPLACE_ID } from "@swiggy-private/cart-types";

// eslint-disable-next-line @typescript-eslint/naming-convention
interface GiftingCartState {
    loading: boolean;
    localCartState: GiftingLocalCart | null;
    reviewedCartState: GiftingReviewedCart | null;
    error: GiftingCartStateData["error"];
}

export type ResultGiftingCartData = Result<ApiResponse<GiftingCartApiResponse>, Error | ApiResponse>;

export type ResultNativeLocalGiftingCartData = Result<ApiResponse<GiftingLocalCart>, Error | ApiResponse>;

async function getGiftingCartDetails(apiHost: string): Promise<ResultGiftingCartData> {
    return getGiftingCart(apiHost)
        .then(data => {
            if (!data) {
                throw new Error(GIFTING_CART_API_DEFAULT_ERROR_MESSAGES.RECEIVED_EMPTY_DATA);
            }
            return Ok(data);
        })
        .catch(err => {
            if (isNetworkError(err)) {
                return Promise.reject(Err(err));
            }
            return Err(err) as ResultGiftingCartData;
        });
}

async function updateGiftingCartDetails(apiHost: string, params: GiftingLocalCart): Promise<ResultGiftingCartData> {
    return updateGiftingCart(apiHost, params)
        .then(data => {
            if (!data) {
                throw new Error(GIFTING_CART_API_DEFAULT_ERROR_MESSAGES.RECEIVED_EMPTY_DATA);
            }
            return Ok(data);
        })
        .catch(err => {
            if (isNetworkError(err)) {
                return Promise.reject(Err(err));
            }
            if (err.status == HTTP_STATUS_CODES.TOO_MANY_REQUESTS) {
                return Promise.reject(Err(err));
            }
            return Err(err) as ResultGiftingCartData;
        });
}

interface GiftingCartError {
    httpStatusCode?: number;
    errorPayload?: unknown;
}

interface GiftingCartStateData {
    loading: boolean;
    data?: {
        localCartState: GiftingLocalCart | null;
        reviewedCartState: GiftingReviewedCart | null;
    };
    error?: GiftingCartError;
}

const useFetchGiftingCart = (): GiftingCartState => {
    const apiContextValue = useContext(ApiContext);
    const apiCallRef = useRef(false);
    const isMountedRef = useMountedRef();
    const [state, setState] = useState<GiftingCartStateData>({ loading: true });
    const { checkoutState } = useContext(CheckoutContext);

    const apiHost = apiContextValue.host.unwrap();

    const updateCartApiResponsehandler = useCallback(
        (response, localCart) => {
            if (isEmpty(response)) return;
            apiCallRef.current = false;
            const reviewedCart = response.unwrapOr(UPDATE_GIFTING_CART_API_DEFAULT_ERROR) ?? null;

            if (isMountedRef.current) {
                setState({
                    loading: false,
                    data: {
                        reviewedCartState: reviewedCart,
                        localCartState: !isEmpty(reviewedCart.data)
                            ? generateNativeLocalGiftingCartFromStates(reviewedCart.data, localCart)
                            : null,
                    },
                    error: undefined,
                });
            }
        },
        [isMountedRef],
    );

    const updateCartApiErrorhandler = useCallback(err => {
        const errorObj: GiftingCartError = {
            errorPayload: err?.error,
        };
        setState({ loading: false, error: errorObj });
        apiCallRef.current = false;
    }, []);

    const getCartData = useCallback(() => {
        if (checkoutState.type === CheckoutActionTypes.SHOW_GIFTING_CART && checkoutState.payload?.skipCartReload) {
            setState({ loading: false, data: state.data, error: state.error });
            return;
        }
        const __IS_TEST__ = localStorage.getItem("__test_get_cart__");
        if (__IS_TEST__) {
            getGiftingCartDetails(apiHost)
                .then(cartResponse => {
                    apiCallRef.current = false;
                    const cartData = cartResponse.unwrapOr(UPDATE_GIFTING_CART_API_DEFAULT_ERROR) ?? null;
                    if (isMountedRef.current) {
                        setState({
                            loading: false,
                            data: {
                                reviewedCartState: cartData,
                                localCartState: !isEmpty(cartData.data)
                                    ? generateNativeLocalGiftingCartFromStates(
                                          cartData.data,
                                          state.data?.localCartState,
                                      )
                                    : null,
                            },
                            error: undefined,
                        });
                    }
                })
                .catch(err => {
                    updateCartApiErrorhandler(err);
                });
            return;
        }

        getBridgeSdk()
            .getGiftingLocalCart({
                tenant: {
                    marketplaceBusinessline: MARKETPLACE_BUSINESS_LINE.GIFTING,
                    marketplaceCategory: MARKETPLACE_CATEGORY.GIFTING,
                    marketplaceId: MARKETPLACE_ID.SWIGGY,
                },
            })
            .then(localCart => {
                if (!localCart) {
                    throw new Error(GIFTING_CART_API_DEFAULT_ERROR_MESSAGES.RECEIVED_EMPTY_DATA_FROM_NATIVE);
                }
                const localCartPayload = convertNativeGiftingCartToUpdateCartAPIPayload(localCart);
                updateGiftingCartDetails(apiHost, localCartPayload)
                    .then(response => {
                        updateCartApiResponsehandler(response, localCartPayload);
                    })
                    .catch(err => {
                        const httpStatusCode = err?.error?.status;
                        const errorObj: GiftingCartError = {
                            errorPayload: err.error,
                        };
                        if (httpStatusCode === HTTP_STATUS_CODES.TOO_MANY_REQUESTS) {
                            errorObj.httpStatusCode = httpStatusCode;
                        }
                        apiCallRef.current = false;
                        setState({
                            loading: false,
                            error: errorObj,
                        });
                        console.error(err);
                    });
            })
            .catch(err => {
                updateCartApiErrorhandler(err);
            });
    }, [
        checkoutState,
        state.data,
        state.error,
        apiHost,
        updateCartApiErrorhandler,
        updateCartApiResponsehandler,
        isMountedRef,
    ]);

    useEffect(() => {
        if (!state.loading || apiCallRef.current) {
            return;
        }
        apiCallRef.current = true;
        getCartData();
    }, [getCartData, state.loading, updateCartApiResponsehandler]);

    return {
        localCartState: state.data?.localCartState ?? null,
        reviewedCartState: state.data?.reviewedCartState ?? null,
        loading: state.loading,
        error: state.error,
    };
};

export default useFetchGiftingCart;
